import type { ReactNode } from 'react';
import {
  AlertTriangle, Ban, CalendarCheck, Check, ChevronRight, Clock, FileText, Repeat, Search, Siren, User, type LucideIcon,
} from 'lucide-react';
import type { Demande } from '../../types';
import { OrigineBadge, TypeBadge } from './StatutBadge';
import { heureCourte, horaireCourt } from './horaire';
import { LIBELLES_TYPE, META_ETAT_BADGE, META_ETAT_LABEL, STATUT_META, formatDateLongue, type MetaEtat } from './format';
import { libelleEcheance, NIVEAU_URGENCE_INFO, type SeuilsUrgence } from './urgence';
import {
  bordCarte, etatFrise, gestionDoitAgir, nomCourt, phraseCarte, phraseCarteGestion, quiAgit, remplacantEnVigueur, urgenceDemande,
  type ElementJour, type Trou, type UrgenceCarte, type VoixCarte,
} from './elementsJour';

// Le calendrier du Tableau de bord en cartes : une carte par élément d'une case (demande ou séance à couvrir), avec son état, sa progression et
// une phrase qui dit où elle en est. « Mes demandes » : maquette retenue le 20/10/2026 (proposition B ; référence : planche « Les 21 états »,
// DECISIONS §25). La gestion voit la MÊME carte (DECISIONS §27) : `voix="gestion"` ajoute la personne absente et son/sa remplaçant·e, et écrit la
// phrase à la 3e personne.
// Chaque carte est un bouton dont le nom lu à voix haute est son texte visible, complété par du texte réservé aux lecteurs d'écran
// (date, « étape N sur 7 », « dans N jours ») — jamais un aria-label qui remplacerait ce qu'on voit.

/** Une icône par méta-état : jamais la couleur seule. */
const ICONE_META: Record<MetaEtat, LucideIcon> = {
  a_traiter: FileText, recherche: Search, urgence: Siren, finalisation: User, passation: CalendarCheck, termine: Check, sans_suite: Ban,
};

function Sr({ children }: { children: ReactNode }) { return <span className="cal-sr">{children}</span>; }

/** « jeudi 22 octobre » (sans l'année) pour le lecteur d'écran. */
export function jourSansAnnee(date: string): string {
  return formatDateLongue(date.slice(0, 10)).replace(/ \d{4}$/, '');
}

function dansJours(j: number): string {
  return j === 0 ? "aujourd'hui" : j === 1 ? 'demain' : `dans ${j} jours`;
}

function PointPole({ couleur }: { couleur?: string | null }) {
  return <span className="pole-dot cal-dot" style={{ background: couleur ?? 'var(--line-strong)' }} aria-hidden="true" />;
}

/** Ligne 1 : le point du pôle, l'atelier (deux lignes au plus), l'heure — puis, pour le lecteur d'écran, la date. */
function LigneAtelier({ couleur, nom, heure, date }: { couleur?: string | null; nom: string; heure: string; date: string }) {
  return (
    <>
      <span className="cal-l1"><PointPole couleur={couleur} /><span className="cal-nom">{nom}</span>{heure && <span className="cal-h">{heure}</span>}</span>
      <Sr>{`, ${jourSansAnnee(date)}.`}</Sr>
    </>
  );
}

/** Gestion : qui est absent·e, et qui le/la remplace (« Alexia S. → David V. »). Les noms entiers sont lus à voix haute. */
function LignePersonnes({ absent, remplacant }: { absent?: string | null; remplacant?: string | null }) {
  if (!absent) return null;
  return (
    <>
      <span className="cal-qui" aria-hidden="true">{nomCourt(absent)}{remplacant ? ` → ${nomCourt(remplacant)}` : ''}</span>
      <Sr>{` Absent·e : ${absent}${remplacant ? `, remplacé·e par ${remplacant}` : ''}.`}</Sr>
    </>
  );
}

/** Gestion : le/la titulaire d'une séance à couvrir. */
function LigneTitulaire({ nom }: { nom: string }) {
  if (!nom || nom === '—') return null;
  return (
    <>
      <span className="cal-qui" aria-hidden="true">Titulaire : {nomCourt(nom)}</span>
      <Sr>{` Titulaire : ${nom}.`}</Sr>
    </>
  );
}

/** Les badges d'une demande : l'état (celui de la liste), « Repérée dans Tipee » à TOUS les états, le type d'urgence, l'échéance si urgent. */
export function BadgesDemande({ d, urgence }: { d: Demande; urgence: UrgenceCarte | null }) {
  const meta = STATUT_META[d.statut];
  const Icone = ICONE_META[meta];
  const typeUrgence = d.type !== 'planifie';
  return (
    <>
      {meta === 'urgence' && typeUrgence
        // Le type (« Urgence semaine ») remplace le badge « Urgence » au lieu de le répéter.
        ? <span className="badge badge-err"><Icone className="cal-icone" aria-hidden="true" />{LIBELLES_TYPE[d.type]}</span>
        : <>
            <span className={META_ETAT_BADGE[meta]}><Icone className="cal-icone" aria-hidden="true" />{META_ETAT_LABEL[meta]}</span>
            {typeUrgence && <TypeBadge type={d.type} />}
          </>}
      <OrigineBadge source={d.source} />
      {urgence?.urgent && (
        <>
          <span className="badge badge-err"><AlertTriangle className="cal-icone" aria-hidden="true" />{NIVEAU_URGENCE_INFO.urgent.label} · {libelleEcheance(urgence.joursRestants)}</span>
          <Sr>{` (${dansJours(urgence.joursRestants)})`}</Sr>
        </>
      )}
    </>
  );
}

function classeBord(bord: ReturnType<typeof bordCarte>): string {
  return bord === 'urgent' ? ' cal-carte--urgent' : bord === 'areg' ? ' cal-carte--areg' : '';
}

export interface CarteDemandeProps {
  demande: Demande;
  maintenant: Date;
  seuils: SeuilsUrgence;
  /** Ouvre le détail du jour sur cette demande (par-dessus le calendrier). */
  onOuvrir: (cle: string, date: string) => void;
  /** Qui regarde : la personne absente (par défaut) ou la gestion, qui voit aussi qui est absent·e. */
  voix?: VoixCarte;
}

export function CarteDemande({ demande: d, maintenant, seuils, onOuvrir, voix = 'personne' }: CarteDemandeProps) {
  const gestion = voix === 'gestion';
  const urgence = urgenceDemande(d, maintenant, seuils);
  const frise = etatFrise(d.statut, d.date_atelier, maintenant);
  const date = d.date_atelier.slice(0, 10);
  // La personne lit « à vous de jouer » ; la gestion lit « suivi par l'équipe gestion » (les mots de la liste), seulement quand c'est à elle de jouer.
  const aAgir = gestion ? gestionDoitAgir(d, maintenant) : quiAgit(d, false, maintenant).startsWith('à vous de jouer');
  const mention = gestion ? quiAgit(d, true, maintenant) : 'à vous de jouer';
  return (
    <button type="button" className={`cal-carte${classeBord(bordCarte(urgence))}`} data-cle={`d-${d.id}`} data-date={date} onClick={() => onOuvrir(`d-${d.id}`, date)}>
      <LigneAtelier couleur={d.pole_couleur} nom={d.atelier_nom ?? 'Atelier'} heure={heureCourte(d.atelier_heure_debut)} date={date} />
      {gestion && <LignePersonnes absent={d.absent_nom} remplacant={remplacantEnVigueur(d)} />}
      <span className="cal-l2"><BadgesDemande d={d} urgence={urgence} /></span>
      {frise.phase != null && (
        <span className="cal-l3">
          <span className="frise-mini-points cal-frise" aria-hidden="true">{frise.traits.map((etat, i) => <i key={i} className={etat} />)}</span>
          {frise.termine
            ? <Sr>Les 7 étapes sont faites.</Sr>
            : <span className="cal-phase"><Sr>{`Étape ${frise.numero} sur ${frise.total} : `}</Sr>{frise.nom}{aAgir && <span className="cal-avous"> : {mention}</span>}</span>}
        </span>
      )}
      <span className="cal-phrase">{gestion ? phraseCarteGestion(d, maintenant) : phraseCarte(d, maintenant)}<ChevronRight className="cal-icone" aria-hidden="true" /></span>
    </button>
  );
}

export interface CarteTrouProps {
  trou: Trou;
  maintenant: Date;
  seuils: SeuilsUrgence;
  /** Séance à venir : le clic crée la demande (l'assistant, prérempli). */
  onCreer: (t: Trou) => void;
  /** Séance passée : le clic ouvre le détail du jour (rien à créer). */
  onOuvrir: (cle: string, date: string) => void;
  voix?: VoixCarte;
}

/** Séance à couvrir : aucune demande. À venir → « Créer la demande » ; passée → plus rien à créer. */
export function CarteTrou({ trou: t, onCreer, onOuvrir, voix = 'personne' }: CarteTrouProps) {
  const cle = `t-${t.atelierId}-${t.date}`;
  const heure = heureCourte(t.heureDebut);
  const titulaire = voix === 'gestion' ? <LigneTitulaire nom={t.titulaireNom} /> : null;
  if (t.joursRestants < 0) {
    return (
      <button type="button" className="cal-carte cal-carte--passee" data-cle={cle} data-date={t.date} onClick={() => onOuvrir(cle, t.date)}>
        <LigneAtelier couleur={t.poleCouleur} nom={t.atelierNom} heure={heure} date={t.date} />
        {titulaire}
        <span className="cal-l2"><span className="badge"><Clock className="cal-icone" aria-hidden="true" />Séance passée</span></span>
        <span className="cal-phrase">Sans demande de remplacement<ChevronRight className="cal-icone" aria-hidden="true" /></span>
      </button>
    );
  }
  const urgent = t.niveau === 'urgent';
  return (
    <button type="button" className={`cal-carte cal-carte--trou${classeBord(urgent ? 'urgent' : 'areg')}`} data-cle={cle} data-date={t.date} data-creer="1" onClick={() => onCreer(t)}>
      <LigneAtelier couleur={t.poleCouleur} nom={t.atelierNom} heure={heure} date={t.date} />
      {titulaire}
      <span className="cal-l2">
        <span className={`badge ${urgent ? 'badge-err' : 'badge-warn'}`}><AlertTriangle className="cal-icone" aria-hidden="true" />À couvrir · {libelleEcheance(t.joursRestants)}</span>
        <Sr>{` (${dansJours(t.joursRestants)})`}</Sr>
      </span>
      <span className="cal-phrase cal-cta">{urgent ? 'Urgent : créer la demande' : 'Créer la demande'}<ChevronRight className="cal-icone" aria-hidden="true" /></span>
    </button>
  );
}

/** Toutes les cartes d'une case, dans l'ordre des heures. « Mes demandes » : VOS séances, trois ou quatre au plus ; la gestion : celles de tout le monde. */
export function CartesDuJour({ elements, maintenant, seuils, onOuvrir, onCreer, voix = 'personne' }: {
  elements: ElementJour[];
  maintenant: Date;
  seuils: SeuilsUrgence;
  onOuvrir: (cle: string, date: string) => void;
  onCreer: (t: Trou) => void;
  voix?: VoixCarte;
}) {
  if (elements.length === 0) return null;
  return (
    <div className="cal-pile">
      {elements.map((e) => e.kind === 'trou'
        ? <CarteTrou key={e.cle} trou={e.trou} maintenant={maintenant} seuils={seuils} onCreer={onCreer} onOuvrir={onOuvrir} voix={voix} />
        : <CarteDemande key={e.cle} demande={e.demande} maintenant={maintenant} seuils={seuils} onOuvrir={onOuvrir} voix={voix} />)}
    </div>
  );
}

/** Horaire « 14 h – 16 h » + lieu, pour le détail du jour. */
export function horaireEtLieu(heureDebut?: string | null, heureFin?: string | null, lieu?: string | null): string {
  return [horaireCourt(heureDebut, heureFin) || heureCourte(heureDebut), lieu].filter(Boolean).join(' · ');
}
